import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { FirebaseCrashlytics, StackFrame } from "@capacitor-firebase/crashlytics";
import { Capacitor } from "@capacitor/core";
import { CapacitorUtils } from "app/capacitor-utils";
import { UserService } from "app/core/user/user.service";

/**
 * Upper bound on the frames forwarded to Crashlytics.*/
const MAX_STACK_FRAMES = 32;

/**
 * Matches a V8 (Chrome / Android WebView)
 */
const STACK_FRAME_PATTERN = /^\s*at\s+(?:(.*?)\s+\()?(.*?):(\d+):(\d+)\)?\s*$/;

interface NormalizedError {
  message: string;
  stack?: string;
}

/**
 * Thin wrapper around Firebase Crashlytics.
 *
 * The app is Angular inside a WebView, so native Crashlytics on its own only
 * sees native crashes -- a JavaScript exception never kills the Android
 * process and is otherwise invisible. This service is the bridge that turns
 * TypeScript errors into Crashlytics non-fatal reports.
 *
 * Every method is a no-op off-device, and every native call is wrapped so that
 * a missing or misconfigured google-services.json can never take the app down.
 *
 * See docs/firebase-crashlytics.md
 */
@Injectable({ providedIn: "root" })
export class CrashlyticsService {
  private _userService = inject(UserService);

  private get _isAvailable(): boolean {
    return CapacitorUtils.isApp;
  }

  /**
   * Enable collection and attach the context that every later report inherits.
   * Called once at startup from provideCrashlytics().
   */
  async init(): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    await this._safely(() => FirebaseCrashlytics.setEnabled({ enabled: true }));

    await this._safely(() =>
      FirebaseCrashlytics.setCustomKey({
        key: "platform",
        value: Capacitor.getPlatform(),
        type: "string",
      })
    );

    // Tag reports with the signed-in user as soon as one is available, and on every subsequent change.
    // Reading user$ instead of hooking AuthService keeps the auth flow untouched.
    this._userService.user$.subscribe({
      next: (user) => {
        const identifier = user?.userId ?? user?.id ?? user?.username;

        if (identifier) {
          void this.setUserId(String(identifier));
        }
      },
      error: () => {
        // A failing user stream is the auth layer's problem, not ours.
      },
    });
  }

  /**
   * Record a non-fatal report. This is what the global error handler calls for
   * every uncaught Angular error.
   *
   * @param error   the thrown value -- Error, HttpErrorResponse, string, or anything else
   * @param context optional short label describing where it came from
   */
  async recordException(error: unknown, context?: string): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    const { message, stack } = this._normalize(error);

    await this._safely(() =>
      FirebaseCrashlytics.recordException({
        message: context ? `${context}: ${message}` : message,
        stacktrace: this._parseStack(stack),
      })
    );
  }

  /**
   * Add a breadcrumb. Logs are attached to whatever report comes next, which
   * is what makes a crash report explain itself.
   */
  async log(message: string): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    await this._safely(() => FirebaseCrashlytics.log({ message }));
  }

  /**
   * Associate subsequent reports with a user.
   */
  async setUserId(userId: string): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    await this._safely(() => FirebaseCrashlytics.setUserId({ userId }));
  }

  /**
   * Clear the association on sign-out.
   */
  async clearUserId(): Promise<void> {
    await this.setUserId("");
  }

  /**
   * Attach an arbitrary searchable key to subsequent reports.
   */
  async setCustomKey(key: string, value: string): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    await this._safely(() => FirebaseCrashlytics.setCustomKey({ key, value, type: "string" }));
  }

  /**
   * Force a native crash. Verification only -- see docs/firebase-crashlytics.md.
   */
  async crash(message = "PMS test crash"): Promise<void> {
    if (!this._isAvailable) {
      return;
    }

    await this._safely(() => FirebaseCrashlytics.crash({ message }));
  }

  /**
   * Whether the previous run of the app ended in a crash. Useful if we ever
   * want to show a "sorry about that" prompt on restart.
   */
  async didCrashOnPreviousExecution(): Promise<boolean> {
    if (!this._isAvailable) {
      return false;
    }

    const result = await this._safely(() => FirebaseCrashlytics.didCrashOnPreviousExecution());

    return result?.crashed ?? false;
  }

  /**
   * Run a native call, swallowing anything it throws.
   *
   * Crash reporting must never be the reason the app breaks: if Firebase is
   * unconfigured (no google-services.json) the plugin rejects, and that must
   * stay a console warning rather than an unhandled rejection.
   */
  private async _safely<T>(operation: () => Promise<T>): Promise<T | undefined> {
    try {
      return await operation();
    } catch (error) {
      console.warn("[Crashlytics] native call failed", error);

      return undefined;
    }
  }

  /**
   * Reduce any thrown value to a message and, where possible, a stack.
   */
  private _normalize(error: unknown): NormalizedError {
    if (error instanceof HttpErrorResponse) {
      // The default HttpErrorResponse message is generic, so build one that is
      // actually distinguishable in the Crashlytics issue list.
      return {
        message: `HTTP ${error.status} ${error.statusText} - ${error.url ?? "unknown url"}`,
        stack: error.error?.stack,
      };
    }

    if (error instanceof Error) {
      return { message: `${error.name}: ${error.message}`, stack: error.stack };
    }

    if (typeof error === "string") {
      return { message: error };
    }

    try {
      return { message: JSON.stringify(error) };
    } catch {
      return { message: String(error) };
    }
  }

  /**
   * Convert a V8 stack string into the frames the plugin expects. Frames that
   * do not match are dropped rather than guessed at.
   */
  private _parseStack(stack?: string): StackFrame[] {
    if (!stack) {
      return [];
    }

    const frames: StackFrame[] = [];

    for (const line of stack.split("\n")) {
      if (frames.length >= MAX_STACK_FRAMES) {
        break;
      }

      const match = STACK_FRAME_PATTERN.exec(line);

      if (!match) {
        continue;
      }

      frames.push({
        functionName: match[1] || "<anonymous>",
        fileName: match[2],
        lineNumber: Number(match[3]),
      });
    }

    return frames;
  }
}
